// The Vue build version to load with the `import` command
// (runtime-only or standalone) is set via the Vite alias.
import Vue from 'vue'
import { io } from 'socket.io-client'
import VueSocketIOExt from 'vue-socket.io-extended'
import vuexI18n from 'vuex-i18n'
import VueLazyload from 'vue-lazyload'
import DOMPurify from 'dompurify'

// vee-validate config
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate/dist/vee-validate.min.js'
import { regex, required, email, numeric } from 'vee-validate/dist/rules.umd.min.js'
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
extend('required', required)
extend('regex', regex)
extend('email', email)
extend('numeric', numeric)

import 'dayjs/locale/fr' // 'en' is already imported by default
import 'dayjs/locale/de'
import 'dayjs/locale/es'
import 'dayjs/locale/pt'
import 'dayjs/locale/zh'
import 'dayjs/locale/it'
import 'dayjs/locale/nl'
import 'dayjs/locale/ar'

// Directives
import longClickDirective from './directives/longclick'
import outsideClick from './directives/outsideClick'
import focus from './directives/focus'

const longClickInstance = longClickDirective({
  delayBeforeStart: 1000,
  delayBeforeEnd: 1000
})
Vue.directive('longclick', longClickInstance)
Vue.directive('click-outside', outsideClick())
Vue.directive('focus', focus())

// Style
import './assets/styles/chat.css'
const errorImg = 'https://yelda-chat.s3.eu-west-3.amazonaws.com/images/error.png'
const loadingImg = 'https://yelda-chat.s3.eu-west-3.amazonaws.com/images/loading.gif'

import App from './App.vue'

// Importing all the locales
import translationsEn from './locales/en_US.json'
import translationsFr from './locales/fr_FR.json'
import translationsEs from './locales/es_ES.json'
import translationsDe from './locales/de_DE.json'
import translationsPt from './locales/pt_PT.json'
import translationsZhCN from './locales/zh_CN.json'
import translationsZhTW from './locales/zh_TW.json'
import translationsIt from './locales/it_IT.json'
import translationsNl from './locales/nl_NL.json'
import translationsArAE from './locales/ar_AE.json'

// Libs
import {
  getSocketTransportOptions,
  getUrlParam,
  loadCssAsync,
  getDomainConfig,
  getEndpoint,
  parseJsonSafely
} from './lib/utils'
import { getItem } from './lib/localStorage'

// Importing config
import config from './config/index'

// import router from './router'
import store from './store'

/**
 * URL PARAMS:
 *  - assistantId: Assistant identifier
 *  - assistantSlug: Assistant slug
 *  - isAdmin: If we show admin functionality
 *  - isStartBtn: If we show the start button overlay for onboarding
 *  - shouldBeOpened: If the chat is open by default
 *  - canBeClosed: If we show the cross(X) to close the chat
 *  - isDemo: If the conversation comes from a demo page
 *  - isKiosk: If the webchat is loaded on kiosk platform
 *  - isWebView: If the webchat is loaded on Android / IOS Mobile app
 */
const assistantId = getUrlParam('assistantId', '5b7edb2b1060312cfeaa791f')
const assistantSlug = getUrlParam('assistantSlug', 'sodebo')
let isAdmin = false
let isStartBtn = false
let shouldBeOpened = false
let canBeClosed = true
let isDemo = false
let isKiosk = false
let isWebView = false
let isAndroidWebView = false
// deduce constants
const domainConfig = getDomainConfig(window.location.host)
const endpoint = getEndpoint(assistantId, assistantSlug, domainConfig)

try {
  isAdmin = Boolean(parseJsonSafely(getUrlParam('isAdmin', false), false))
  store.dispatch('set_user_admin', isAdmin)

  isStartBtn = Boolean(parseJsonSafely(getUrlParam('isStartBtn', false), false))
  shouldBeOpened = Boolean(parseJsonSafely(getUrlParam('shouldBeOpened', false), false))
  canBeClosed = Boolean(parseJsonSafely(getUrlParam('canBeClosed', true), true))
  isDemo = Boolean(parseJsonSafely(getUrlParam('isDemo', false), false))
  isKiosk = Boolean(parseJsonSafely(getUrlParam('isKiosk', false), false))
  isWebView = Boolean(parseJsonSafely(getUrlParam('isWebview', false), false))

  if (isWebView) {
    // Without the information isWebView we would test
    // /wv/.test(navigator.userAgent) || (/\bAndroid\b/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent))
    // but isWebView we already know that we are in a webview so no need of  !/Chrome/
    isAndroidWebView = /wv/.test(navigator.userAgent) || /\bAndroid\b/.test(navigator.userAgent)

    /**
     * If this detection methods not works then alternative methods are
     *
     * If the webview injected with Bridge
     * typeof AndroidInterface !== "undefined"
     *
     * If it is React Native application
     * !!window.ReactNativeWebView
     */
  }
  store.dispatch('set_is_demo', isDemo)

  // Load platform CSS only if we are on the demo page
  if (isDemo) {
    for (const platform of config.PLATFORMS_SIMULATED) {
      loadCssAsync(platform)
    }
  }
} catch (error) {
  console.error('Error while parsing url params', error)
}

const platformSimulated = getUrlParam('platformSimulated', null)
const yparam = getUrlParam('yparam', null)
let locale = getUrlParam('locale', config.DEFAULT_LOCALE)
locale = locale === null || config.LOCALES.indexOf(locale) < 0 ? config.DEFAULT_LOCALE : locale
const localeCode = locale.slice(0, 2)
const formattedLocale = locale.toLowerCase().replace('_', '-')
const isRTL = config.RTL_LOCALES.includes(locale)
Vue.use(vuexI18n.plugin, store)
Vue.use(config)
Vue.use(VueLazyload, {
  error: errorImg,
  loading: loadingImg
})
const sanitizeConfig = {
  ADD_TAGS: ['img', 'button', 'label', 'del', 'ins', 'input', 'details', 'summary'],
  ADD_ATTR: [
    'class',
    'style',
    'id',
    'title',
    'role',
    'for',
    'tabindex',
    'name',
    'rel',
    'target',
    'width',
    'height',
    'src',
    'alt',
    'href',
    'type',
    'value',
    'checked'
  ],
  ALLOW_DATA_ATTR: true,
  ALLOW_ARIA_ATTR: true
}

Vue.prototype.$sanitize = (value, options = {}) => {
  return DOMPurify.sanitize(value, { ...sanitizeConfig, ...options })
}
Vue.i18n.add('en', translationsEn)
Vue.i18n.add('fr', translationsFr)
Vue.i18n.add('es', translationsEs)
Vue.i18n.add('de', translationsDe)
Vue.i18n.add('pt', translationsPt)
Vue.i18n.add('zh-cn', translationsZhCN)
// Fallback on traditional Chinese if Chinese locale is not the "simplified" one
Vue.i18n.add('zh', translationsZhTW)
Vue.i18n.add('it', translationsIt)
Vue.i18n.add('nl', translationsNl)
Vue.i18n.add('ar-ae', translationsArAE)

// if the long locale exists in the i18n available locales use it instead of the generic one
// For instance, if the locale is zh_CN and i18n has the locale 'zh-cn', use it instead of falling back on the global 'zh'
const localeToSet = Vue.i18n.localeExists(formattedLocale) ? formattedLocale : localeCode
Vue.i18n.set(localeToSet)
store.dispatch('set_locale', locale)
// Setting the Authorization header for socket connection, using the retrieved token
const options = getSocketTransportOptions(assistantId, getItem(config.TOKEN, ''))

const url = `${endpoint}?locale=${locale}`
let SocketInstance

if (!options.extraHeaders || options.extraHeaders.Authorization === '') {
  // Supposed to send first message faster (and would avoid to send multiple chat:load in a row)
  // https://socket.io/docs/client-api/#With-websocket-transport-only
  SocketInstance = io(url, {
    transports: ['websocket']
  })
} else {
  SocketInstance = io(url, options)
}

/**
 * Mutations and actions will be dispatched or committed automatically in the Vuex store when a socket event arrives.
 * A mutation or action must follow the naming convention below to recognize and handle a socket event.
 * A mutation should start with SOCKET_ prefix and continue with an uppercase version of the event
 * An action should start with socket_ prefix and continue with camelcase version of the event
 */
Vue.use(VueSocketIOExt, SocketInstance, {
  store
})

const parentUrl = window.location !== window.parent.location ? document.referrer : document.location.href
const yeldaPush = getUrlParam('yelda_push', '', parentUrl)
store.dispatch('set_yelda_push', yeldaPush)

Vue.config.productionTip = false

/* eslint-disable no-new */
new Vue({
  el: '#app',
  components: {
    App: App
  },
  data: {
    isRTL,
    isAdmin: isAdmin,
    shouldBeOpened: shouldBeOpened,
    canBeClosed: canBeClosed,
    assistantId: assistantId,
    assistantSlug: assistantSlug,
    isStartBtn: isStartBtn
  },
  render(callback) {
    return callback('App', {
      props: {
        isAdmin: this.isAdmin,
        shouldBeOpened: this.shouldBeOpened,
        canBeClosed: this.canBeClosed,
        isRTL: this.isRTL,
        assistantId: this.assistantId,
        assistantSlug: this.assistantSlug,
        isStartBtn: this.isStartBtn,
        pushNotificationWaitTime: domainConfig.NOTIFICATION_WAIT_TIME,
        isYeldaWebDomain: domainConfig.IS_YELDA_WEB,
        yparam: yparam,
        platformSimulated: platformSimulated,
        isKiosk: isKiosk,
        isWebView: isWebView,
        isAndroidWebView: isAndroidWebView
      }
    })
  },
  store,
  template: '<App/>'
})

// Saving the assistantId in the store for saving the token in cookie
store.dispatch('set_assistant_id', assistantId)
// Store the parentUrl to use it in sockets
const location = getUrlParam('location', parentUrl)
store.dispatch('set_parent_url', decodeURIComponent(location))
